import { NavLink, Outlet, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import {
  LayoutDashboard,
  FileText,
  CalendarDays,
  Link2,
  BarChart3,
  Settings,
  ShieldCheck,
  CreditCard,
  Palette,
  Menu,
  LogOut,
  PenSquare,
  Lightbulb,
  AtSign,
  PanelLeftClose,
  PanelLeftOpen,
  UserCircle,
  UserCheck,
  Users,
  CheckCircle2,
  Clock,
  Code,
  HelpCircle,
  Sparkles,
  Gift,
  ChevronDown,
  type LucideProps,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../lib/api';
import { cn } from '../lib/cn';

type Item = {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  soon?: boolean;
  adminOnly?: boolean;
  /** Section key for team-member gating. Members only see items whose section
   *  they were granted. Items without a section are always visible to members. */
  section?: string;
  /** Hidden from team members (owner-only area). */
  ownerOnly?: boolean;
  /** Only shown to owners whose plan grants team seats. */
  teamsOwner?: boolean;
};

const items: Item[] = [
  { to: '/dashboard', label: 'Inicio', icon: LayoutDashboard },
  { to: '/dashboard/posts/new', label: 'Crear publicación', icon: PenSquare, section: 'post' },
  { to: '/dashboard/posts', label: 'Publicaciones', icon: FileText, section: 'posts' },
  { to: '/dashboard/approvals', label: 'Aprobaciones', icon: UserCheck, section: 'approvals' },
  { to: '/dashboard/calendar', label: 'Calendario', icon: CalendarDays, section: 'calendar' },
  { to: '/dashboard/analytics', label: 'Analytics', icon: BarChart3, section: 'analytics' },
  { to: '/dashboard/accounts', label: 'Cuentas conectadas', icon: Link2, ownerOnly: true },
  { to: '/dashboard/brand', label: 'Tu marca', icon: Palette, ownerOnly: true },
  { to: '/dashboard/link-in-bio', label: 'Link in bio', icon: AtSign, section: 'bio' },
  { to: '/dashboard/sorteos', label: 'Sorteos', icon: Gift, section: 'post' },
  { to: '/dashboard/inspiration', label: 'Inspiración', icon: Lightbulb, section: 'inspiration' },
  { to: '/dashboard/autopost', label: 'Autopost', icon: Sparkles, adminOnly: true },
  { to: '/admin', label: 'Administración', icon: ShieldCheck, adminOnly: true },
];

const COLLAPSE_KEY = 'zp:sidebar:collapsed';

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  });

  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  type SubInfo = {
    entitled: boolean;
    status: string;
    trialEndsAt: string | null;
    currentPeriodEnd: string | null;
    plan: { name: string } | null;
  };
  const { data: subData } = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => api<{ subscription: SubInfo | null }>('/api/billing/subscription'),
  });
  const sub = subData?.subscription ?? null;
  const noPlan = subData !== undefined && (!sub || !sub.entitled);

  // Hard paywall: no free access. Until the workspace is entitled (active/trial
  // subscription), the only reachable dashboard route is Billing, where they
  // start their card-on-file trial. `user.entitled` comes from /api/auth/me and
  // is team-aware (members inherit the owner's plan). Undefined → treat as locked.
  const locked = user != null && user.entitled !== true;
  const onBilling = location.pathname.startsWith('/dashboard/billing');

  const { data: unreadData } = useQuery({
    queryKey: ['support', 'unread-count'],
    queryFn: () => api<{ count: number }>('/api/support/unread-count'),
    refetchInterval: 60_000,
  });
  const supportUnread = unreadData?.count ?? 0;

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  const team = user?.team;
  const isMember = team?.role === 'member';
  const memberSections = team?.role === 'member' ? team.sections : [];
  const ownerCanUseTeams = team?.role === 'owner' ? team.canUseTeams : false;

  const visibleItems = items.filter((i) => {
    if (i.adminOnly && user?.role !== 'admin') return false;
    if (i.teamsOwner && !ownerCanUseTeams) return false;
    if (isMember) {
      if (i.ownerOnly) return false;
      if (i.section && !memberSections.includes(i.section)) return false;
    }
    return true;
  });

  // Secondary / utility items (teams, help, API, billing, settings, profile).
  // Rendered inside the same scroll area as the primary nav so everything
  // scrolls as one unit and future items just extend the list.
  type SecondaryItem = { to: string; label: string; Icon: ComponentType<LucideProps>; badge: number };
  const secondaryItems: SecondaryItem[] = (
    [
      ...(ownerCanUseTeams ? [{ to: '/dashboard/equipos', label: 'Equipos', Icon: Users, badge: 0 }] : []),
      { to: '/dashboard/help', label: 'Ayuda', Icon: HelpCircle, badge: supportUnread },
      { to: '/dashboard/api', label: 'API y MCP', Icon: Code, badge: 0 },
      { to: '/dashboard/billing', label: 'Suscripción', Icon: CreditCard, badge: 0 },
      { to: '/dashboard/settings', label: 'Configuración', Icon: Settings, badge: 0 },
      { to: '/dashboard/profile', label: 'Perfil', Icon: UserCircle, badge: 0 },
    ] as SecondaryItem[]
  ).filter((it) => !isMember || (it.to !== '/dashboard/api' && it.to !== '/dashboard/billing'));

  return (
    <div className="h-screen bg-slate-50 flex overflow-hidden">
      {open && (
        <button
          aria-label="Cerrar menú"
          onClick={() => setOpen(false)}
          className="lg:hidden fixed inset-0 bg-slate-900/40 z-30"
        />
      )}

      <aside
        className={cn(
          'fixed lg:static z-40 inset-y-0 left-0 bg-white border-r border-slate-200 flex flex-col transition-[width,transform] duration-200',
          // mobile: always w-64 when open
          'w-64',
          // desktop: width depends on collapsed
          collapsed ? 'lg:w-16' : 'lg:w-64',
          'lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* logo + collapse toggle */}
        <div
          className={cn(
            'h-16 border-b border-slate-200 flex items-center',
            collapsed ? 'lg:justify-center lg:px-0 px-6' : 'px-6 justify-between',
          )}
        >
          <Link to="/dashboard" className="flex items-center">
            {/* full logo on mobile + desktop-expanded */}
            <img
              src="/zettapost-logo-min.png"
              alt="Zettapost"
              className={cn('h-8 w-auto', collapsed && 'lg:hidden')}
            />
            {/* icon-only on desktop-collapsed */}
            <img
              src="/zetta-post-icon.png"
              alt="Zettapost"
              className={cn('h-8 w-auto hidden', collapsed && 'lg:block')}
            />
          </Link>
          <button
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            className={cn(
              'hidden lg:inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition',
              collapsed && 'lg:hidden',
            )}
          >
            <PanelLeftClose size={16} />
          </button>
        </div>

        {/* floating expand button when collapsed */}
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            aria-label="Expandir menú"
            title="Expandir menú"
            className="hidden lg:inline-flex absolute -right-3 top-[52px] h-6 w-6 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-500 hover:text-brand-600 hover:border-brand-300 shadow-sm transition z-10"
          >
            <PanelLeftOpen size={12} />
          </button>
        )}

        <NavScroll collapsed={collapsed}>
          {visibleItems.map((item) => {
            const Icon = item.icon;
            if (item.soon) {
              return (
                <span
                  key={item.to}
                  className={cn(
                    'flex items-center rounded-lg text-sm text-slate-400 cursor-not-allowed',
                    collapsed ? 'lg:justify-center lg:px-0 lg:py-2 px-3 py-2 justify-between' : 'px-3 py-2 justify-between',
                  )}
                  title={collapsed ? `${item.label} (próximamente)` : 'Próximamente'}
                >
                  <span className="flex items-center gap-3">
                    <Icon size={18} aria-hidden />
                    <span className={cn(collapsed && 'lg:hidden')}>{item.label}</span>
                  </span>
                  <span
                    className={cn(
                      'text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 rounded-full px-2 py-0.5',
                      collapsed && 'lg:hidden',
                    )}
                  >
                    pronto
                  </span>
                </span>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/dashboard' || item.to === '/dashboard/posts'}
                onClick={() => setOpen(false)}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  cn(
                    'flex items-center rounded-lg text-sm font-medium transition',
                    collapsed
                      ? 'lg:justify-center lg:px-0 lg:py-2 lg:gap-0 px-3 py-2 gap-3'
                      : 'px-3 py-2 gap-3',
                    isActive ? 'bg-brand-600 text-white' : 'text-slate-700 hover:bg-slate-100',
                  )
                }
              >
                <Icon size={18} aria-hidden />
                <span className={cn(collapsed && 'lg:hidden')}>{item.label}</span>
              </NavLink>
            );
          })}

          {/* divider between primary nav and utilities */}
          <div className="border-t border-slate-200 my-2" />

          {/* secondary nav: teams + help + API + billing + settings + profile */}
          {secondaryItems.map(({ to, label, Icon, badge }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              title={collapsed ? (badge > 0 ? `${label} (${badge})` : label) : undefined}
              className={({ isActive }) =>
                cn(
                  'relative flex items-center rounded-lg text-sm font-medium transition',
                  collapsed
                    ? 'lg:justify-center lg:px-0 lg:py-2 lg:gap-0 px-3 py-2 gap-3'
                    : 'px-3 py-2 gap-3',
                  isActive ? 'bg-brand-600 text-white' : 'text-slate-700 hover:bg-slate-100',
                )
              }
            >
              <span className="relative inline-flex">
                <Icon size={18} aria-hidden />
                {/* badge sólo cuando el menú está colapsado: dot rojo sobre el icono */}
                {badge > 0 && (
                  <span className={cn(
                    'hidden absolute -top-1.5 -right-1.5 h-4 min-w-4 items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1 ring-2 ring-white',
                    collapsed && 'lg:inline-flex',
                  )}>
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </span>
              <span className={cn('flex-1', collapsed && 'lg:hidden')}>{label}</span>
              {/* badge en línea cuando el menú está expandido */}
              {badge > 0 && (
                <span className={cn(
                  'inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1.5',
                  collapsed && 'lg:hidden',
                )}>
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </NavLink>
          ))}
        </NavScroll>

        {/* Pinned footer — always visible: plan status, account, logout. */}
        <div className={cn('border-t border-slate-200 shrink-0', collapsed ? 'lg:p-2 p-3' : 'p-3')}>
          <PlanBadge sub={sub} collapsed={collapsed} />

          <Link
            to="/dashboard/profile"
            onClick={() => setOpen(false)}
            title={collapsed ? 'Editar perfil' : user?.email}
            className={cn(
              'flex items-center rounded-lg text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition',
              collapsed ? 'lg:justify-center lg:px-0 lg:py-2 px-3 py-2 gap-2' : 'px-3 py-2 gap-2',
            )}
          >
            <UserCircle size={16} className="shrink-0" aria-hidden />
            <span className={cn('truncate', collapsed && 'lg:hidden')}>{user?.email}</span>
          </Link>
          <button
            onClick={handleLogout}
            title={collapsed ? 'Salir' : undefined}
            className={cn(
              'btn-secondary w-full gap-2',
              collapsed ? 'lg:mt-0 mt-1 lg:px-0 lg:justify-center' : 'mt-1',
            )}
          >
            <LogOut size={16} aria-hidden />
            <span className={cn(collapsed && 'lg:hidden')}>Salir</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="lg:hidden h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-20 shrink-0">
          <button
            aria-label="Abrir menú"
            onClick={() => setOpen(true)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-700"
          >
            <Menu size={18} aria-hidden />
          </button>
          <Link to="/dashboard">
            <img src="/zettapost-logo-min.png" alt="Zettapost" className="h-7 w-auto" />
          </Link>
          <span className="w-9" />
        </header>

        <main data-scroll-root className="flex-1 overflow-y-auto">
          {noPlan && !locked && (
            <Link
              to="/dashboard/billing"
              className="block bg-amber-50 border-b border-amber-200 px-6 py-2.5 text-sm text-amber-800 hover:bg-amber-100 transition"
            >
              <span className="font-semibold">No tienes un plan activo.</span>{' '}
              Suscríbete para publicar y conectar cuentas →
            </Link>
          )}
          <div className="px-6 py-8 max-w-6xl w-full mx-auto">
            {locked && !onBilling ? <Navigate to="/dashboard/billing" replace /> : <Outlet />}
          </div>
        </main>
      </div>
    </div>
  );
}

/* ── Scrollable nav area with top/bottom fade + "more" hint ─────────────
 * Holds the whole menu (primary + utilities) so it scrolls as one unit. The
 * gradients only show when there's hidden content in that direction, and a
 * little chevron hints there's more below — so nobody misses items off-screen.
 * Scales automatically as pages are added. */
function NavScroll({ collapsed, children }: { collapsed: boolean; children: ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(true);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setAtTop(el.scrollTop <= 4);
    setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
  }, []);

  useEffect(() => {
    update();
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    ro.observe(content);
    return () => ro.disconnect();
  }, [update]);

  return (
    <div className="relative flex-1 min-h-0">
      {/* top fade — items hidden above */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 h-5 z-10 bg-gradient-to-b from-white to-transparent transition-opacity duration-200',
          atTop ? 'opacity-0' : 'opacity-100',
        )}
      />
      <div ref={scrollRef} onScroll={update} className="h-full overflow-y-auto py-3">
        <div ref={contentRef} className={cn('space-y-1', collapsed ? 'lg:px-2 px-3' : 'px-3')}>
          {children}
        </div>
      </div>
      {/* bottom fade + chevron — more items below */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 h-7 z-10 flex items-end justify-center bg-gradient-to-t from-white via-white/85 to-transparent transition-opacity duration-200',
          atBottom ? 'opacity-0' : 'opacity-100',
        )}
      >
        <ChevronDown size={14} className="mb-1 text-slate-400" />
      </div>
    </div>
  );
}

/* ── Plan badge in sidebar footer ───────────────────────── */
type SubInfo = {
  entitled: boolean;
  status: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  plan: { name: string } | null;
};

function PlanBadge({ sub, collapsed }: { sub: SubInfo | null; collapsed: boolean }) {
  // Days remaining helper
  function daysLeft(iso: string | null): number {
    if (!iso) return 0;
    return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));
  }

  // Active paid plan
  if (sub?.entitled && sub.status === 'active' && sub.plan) {
    return (
      <div
        className={cn(
          'mb-2 flex items-center gap-2 rounded-lg px-3 py-1.5 bg-emerald-50 border border-emerald-200',
          collapsed && 'lg:justify-center lg:px-0',
        )}
        title={collapsed ? `Plan ${sub.plan.name}: Activo` : undefined}
      >
        <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
        <span className={cn('text-xs text-emerald-700 truncate', collapsed && 'lg:hidden')}>
          <span className="text-emerald-500">Plan </span>
          <span className="font-semibold">{sub.plan.name}</span>
          <span className="text-emerald-500">: </span>
          <span className="font-medium">Activo</span>
        </span>
      </div>
    );
  }

  // Trialing
  if (sub?.entitled && sub.status === 'trialing') {
    const days = daysLeft(sub.trialEndsAt);
    return (
      <div
        className={cn(
          'mb-2 flex items-center gap-2 rounded-lg px-3 py-1.5 bg-brand-50 border border-brand-200',
          collapsed && 'lg:justify-center lg:px-0',
        )}
        title={collapsed ? `${days}d de prueba` : undefined}
      >
        <Clock size={13} className="text-brand-600 shrink-0" />
        <span className={cn('text-xs font-medium text-brand-700 truncate', collapsed && 'lg:hidden')}>
          {days === 1 ? '1 día de prueba' : `${days} días de prueba`}
        </span>
      </div>
    );
  }

  // No plan — subscribe CTA
  return (
    <Link
      to="/dashboard/billing"
      className={cn(
        'mb-2 flex items-center gap-2 rounded-lg px-3 py-1.5 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition',
        collapsed && 'lg:justify-center lg:px-0',
      )}
      title={collapsed ? 'Suscribirse' : undefined}
    >
      <CreditCard size={13} className="text-amber-600 shrink-0" />
      <span className={cn('text-xs font-medium text-amber-700 truncate', collapsed && 'lg:hidden')}>
        Suscribirse
      </span>
    </Link>
  );
}
